/**
 * Construcción de los CSV, sin tocar el DOM ni la base de datos: así se puede
 * probar el formato de verdad (una coma decimal mal puesta convierte 1,5 g en
 * 15 g al abrirlo en Excel).
 */

/** Marca de orden de bytes: sin ella, Excel en español destroza los acentos. */
const BOM = '﻿';

/**
 * Lo que Excel y LibreOffice interpretan como FÓRMULA si abre el campo.
 *
 * Un alimento de Open Food Facts llamado «=HYPERLINK(...)» no es ciencia
 * ficción: cualquiera puede editar su base. Al abrir el CSV, la hoja ejecuta
 * eso en el ordenador de quien exporta. Se neutraliza con un apóstrofo, que es
 * lo que recomienda OWASP y lo que la hoja entiende como "esto es texto".
 */
const FORMULA_START = /^[=+\-@\t\r]/;

/** Un número en es-ES: coma decimal. Con punto, Excel lo lee como texto o lo multiplica. */
export function esNumber(value: number): string {
  if (!Number.isFinite(value)) return '';
  // Se redondea a 4 decimales: los flotantes arrastran 0,30000000000000004.
  return String(Math.round(value * 10_000) / 10_000).replace('.', ',');
}

export function csvField(value: unknown): string {
  if (value == null) return '';

  const text =
    typeof value === 'number'
      ? esNumber(value)
      : typeof value === 'string'
        ? value
        : typeof value === 'boolean'
          ? value
            ? 'sí'
            : 'no'
          : JSON.stringify(value);

  const safe = FORMULA_START.test(text) ? `'${text}` : text;
  // La coma NO entrecomilla: con separador ';' no rompe nada, y un «0,3»
  // entrecomillado lo lee Excel como texto, que es justo lo que se quería
  // evitar al poner la coma decimal.
  return /["\n\r;]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function toCsv(headers: readonly string[], rows: readonly unknown[][]): string {
  // Separador ';': es lo que Excel en español abre sin preguntar nada, y deja
  // la coma libre para los decimales.
  const lines = [headers.join(';'), ...rows.map((row) => row.map(csvField).join(';'))];
  return BOM + lines.join('\r\n');
}

/** Lee un CSV generado aquí. Existe para los tests de ida y vuelta. */
export function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const clean = text.startsWith(BOM) ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;

  for (let i = 0; i < clean.length; i++) {
    const char = clean[i]!;

    if (quoted) {
      if (char === '"' && clean[i + 1] === '"') {
        field += '"';
        i++;
      } else if (char === '"') {
        quoted = false;
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') quoted = true;
    else if (char === ';') {
      row.push(field);
      field = '';
    } else if (char === '\r') continue;
    else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += char;
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return { headers: rows[0] ?? [], rows: rows.slice(1) };
}
